import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import {
  CreateRoomParams,
  LoginInMeeting,
  LoginInMeetingResponse,
  MeetingRoom,
  CreateEditMeetingRoom,
  MeetingChat,
  CreateEditChat,
  ChatCandidate,
  LastMessage,
  RoomConnectParams,
  SendMessage,
  GetMessages,
  GetMessagesParams,
  DocProps,
  MeetingSettingsApi,
  RegisterParams,
  CurrentUser,
  MeetingHistoryItem,
  Classroom,
  ClassroomLinks,
  GroupCalendar,
  GroupCreateParams,
  GroupAssistant,
  GroupDetail,
  GroupLessonsResponse,
  GroupListResponse,
  GroupMembersResponse,
  GroupUpdateParams,
  InviteStatus,
  LessonAttendance,
  LessonStartResponse,
  LessonStatus,
  MyGroup,
  CreateMeetingSettingTemplate,
  MeetingSettingTemplateApi,
  ApiErrorResponse,
  AdminMeetingItem,
  AdminUserItem,
  AdminClassroomItem,
  AdminListResponse,
  BoardSnapshotItem
} from '@/types/api.ts';
import { showToast, clearToast } from '@/components/ToastManager';
import { ParticipantRole } from '@/consts/meeting';

declare module 'axios' {
  interface AxiosRequestConfig {
    // Handle the error at the call site instead of showing the global toast
    // (e.g. for polling, where a toast every few seconds would be noise).
    skipErrorToast?: boolean;
  }
}

class Api {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError<ApiErrorResponse>) => {
        const isLoginError = error.config?.url?.includes('/auth/jwt/login');
        const isCancelled = error.code === AxiosError.ERR_CANCELED;
        const skipToast = error.config?.skipErrorToast;
        const isMeetingFull =
          error.response?.status === 403 && error.response.data.error === 'MEETING_FULL';
        if (!isLoginError && !isCancelled && !skipToast && !isMeetingFull) {
          showToast('errorToast', 'error', 'Ошибка запроса', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  // ── Authentication (fastapi-users, mounted at /api/v1/auth) ────────────────

  public async register({ email, password, role }: RegisterParams): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.post('/auth/register', {
        email,
        password,
        role
      });
      return data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw new Error('Error registering user');
    }
  }

  public async login(email: string, password: string): Promise<void> {
    try {
      // fastapi-users /auth/jwt/login expects an OAuth2 password-flow form body
      // (`username` + `password`), not JSON. The JWT comes back in an HttpOnly
      // `auth_token` cookie, so there is nothing to return.
      const body = new URLSearchParams();
      body.append('username', email);
      body.append('password', password);
      await this.axiosInstance.post('/auth/jwt/login', body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/jwt/logout');
    } catch (error) {
      console.error('Error logging out:', error);
      throw new Error('Error logging out');
    }
  }

  public async forgotPassword(email: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/forgot-password', { email });
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw new Error('Error requesting password reset');
    }
  }

  public async resetPassword(token: string, password: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/reset-password', { token, password });
    } catch (error) {
      console.error('Error resetting password:', error);
      throw new Error('Error resetting password');
    }
  }

  public async getCurrentUser(): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.get('/auth/users/me');
      return data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw new Error('Error getting current user');
    }
  }

  /**
   * Check the optional account cookie during application startup.
   */
  public async probeCurrentUser(): Promise<CurrentUser | null> {
    const { data } = await this.axiosInstance.get<CurrentUser | null>('/auth/session');
    return data;
  }

  // ── Study groups (classrooms) ──────────────────────────────────────────────
  // Methods whose 409/422 the UI shows next to a field skip the global toast
  // and rethrow the AxiosError as is (see apiErrorDetail).

  public async getGroups(archived: boolean): Promise<GroupListResponse> {
    try {
      const { data } = await this.axiosInstance.get<GroupListResponse>('/classrooms/', {
        params: { archived }
      });
      return data;
    } catch (error) {
      console.error('Error getting groups:', error);
      throw new Error('Error getting groups');
    }
  }

  /** Groups where the current user is a member or the assistant. */
  public async getMyGroups(): Promise<MyGroup[]> {
    try {
      const { data } = await this.axiosInstance.get<MyGroup[]>('/classrooms/my');
      return data;
    } catch (error) {
      console.error('Error getting my groups:', error);
      throw new Error('Error getting my groups');
    }
  }

  /** null when the group doesn't exist or the user isn't related to it. */
  public async getGroup(classroomId: string): Promise<GroupDetail | null> {
    try {
      const { data } = await this.axiosInstance.get<GroupDetail>(`/classrooms/${classroomId}`, {
        skipErrorToast: true
      });
      return data;
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 404 || status === 422) {
        return null;
      }
      console.error('Error getting group:', error);
      throw error;
    }
  }

  public async getGroupMembers(classroomId: string): Promise<GroupMembersResponse> {
    try {
      const { data } = await this.axiosInstance.get<GroupMembersResponse>(
        `/classrooms/${classroomId}/members`
      );
      return data;
    } catch (error) {
      console.error('Error getting group members:', error);
      throw new Error('Error getting group members');
    }
  }

  public async getGroupLessons(
    classroomId: string,
    offset: number,
    limit: number,
    // "YYYY-MM-DD" in the group's zone: only the lessons started that day
    day: string | null = null
  ): Promise<GroupLessonsResponse> {
    try {
      const { data } = await this.axiosInstance.get<GroupLessonsResponse>(
        `/classrooms/${classroomId}/lessons`,
        { params: { offset, limit, ...(day && { day }) } }
      );
      return data;
    } catch (error) {
      console.error('Error getting group lessons:', error);
      throw new Error('Error getting group lessons');
    }
  }

  public async getLessonAttendance(
    classroomId: string,
    meetingUuid: string
  ): Promise<LessonAttendance> {
    try {
      const { data } = await this.axiosInstance.get<LessonAttendance>(
        `/classrooms/${classroomId}/lessons/${meetingUuid}/attendance`
      );
      return data;
    } catch (error) {
      console.error('Error getting lesson attendance:', error);
      throw new Error('Error getting lesson attendance');
    }
  }

  /** month: "YYYY-MM" */
  public async getGroupCalendar(classroomId: string, month: string): Promise<GroupCalendar> {
    try {
      const { data } = await this.axiosInstance.get<GroupCalendar>(
        `/classrooms/${classroomId}/calendar`,
        { params: { month }, skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error getting group calendar:', error);
      throw new Error('Error getting group calendar');
    }
  }

  public async createGroup(params: GroupCreateParams): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.post<Classroom>('/classrooms/', params, {
        skipErrorToast: true
      });
      return data;
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  public async updateGroup(classroomId: string, params: GroupUpdateParams): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.patch<Classroom>(
        `/classrooms/${classroomId}`,
        params,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  public async deleteGroup(classroomId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/classrooms/${classroomId}`);
    } catch (error) {
      console.error('Error deleting group:', error);
      throw new Error('Error deleting group');
    }
  }

  public async archiveGroup(classroomId: string): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.post<Classroom>(
        `/classrooms/${classroomId}/archive`
      );
      return data;
    } catch (error) {
      console.error('Error archiving group:', error);
      throw new Error('Error archiving group');
    }
  }

  /** 409 when the owner is at the active-group limit. */
  public async unarchiveGroup(classroomId: string): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.post<Classroom>(
        `/classrooms/${classroomId}/unarchive`,
        undefined,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error unarchiving group:', error);
      throw error;
    }
  }

  public async resetStudentCode(classroomId: string): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.post<Classroom>(
        `/classrooms/${classroomId}/reset-student-code`
      );
      return data;
    } catch (error) {
      console.error('Error resetting lesson link:', error);
      throw new Error('Error resetting lesson link');
    }
  }

  public async resetInviteCode(classroomId: string): Promise<Classroom> {
    try {
      const { data } = await this.axiosInstance.post<Classroom>(
        `/classrooms/${classroomId}/reset-invite-code`
      );
      return data;
    } catch (error) {
      console.error('Error resetting invite link:', error);
      throw new Error('Error resetting invite link');
    }
  }

  /** Adds one more assistant; the others stay. 422 with a message for the
   * email field (unknown user, owner, member). */
  public async addAssistant(classroomId: string, email: string): Promise<GroupAssistant> {
    try {
      const { data } = await this.axiosInstance.post<GroupAssistant>(
        `/classrooms/${classroomId}/assistants`,
        { email },
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error adding assistant:', error);
      throw error;
    }
  }

  public async removeAssistant(classroomId: string, userId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/classrooms/${classroomId}/assistants/${userId}`);
    } catch (error) {
      console.error('Error removing assistant:', error);
      throw new Error('Error removing assistant');
    }
  }

  public async removeGroupMember(classroomId: string, userId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/classrooms/${classroomId}/members/${userId}`);
    } catch (error) {
      console.error('Error removing group member:', error);
      throw new Error('Error removing group member');
    }
  }

  public async renameGroupLesson(
    classroomId: string,
    meetingUuid: string,
    title: string
  ): Promise<void> {
    try {
      await this.axiosInstance.patch(`/classrooms/${classroomId}/lessons/${meetingUuid}`, {
        title
      });
    } catch (error) {
      console.error('Error renaming lesson:', error);
      throw new Error('Error renaming lesson');
    }
  }

  public async removeGroupLesson(classroomId: string, meetingUuid: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/classrooms/${classroomId}/lessons/${meetingUuid}`);
    } catch (error) {
      console.error('Error removing lesson:', error);
      throw new Error('Error removing lesson');
    }
  }

  /** A member leaves the group; the assistant gives up the role. */
  public async leaveGroup(classroomId: string): Promise<void> {
    try {
      await this.axiosInstance.delete(`/classrooms/${classroomId}/membership`);
    } catch (error) {
      console.error('Error leaving group:', error);
      throw new Error('Error leaving group');
    }
  }

  /** null when the invite code is unknown or was reset. */
  public async getInviteStatus(code: string): Promise<InviteStatus | null> {
    try {
      const { data } = await this.axiosInstance.get<InviteStatus>(
        `/invites/${encodeURIComponent(code)}`,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      console.error('Error getting invite status:', error);
      throw new Error('Error getting invite status');
    }
  }

  /** 409 for an archived or full group. */
  public async acceptInvite(code: string): Promise<{ classroom_id: string }> {
    try {
      const { data } = await this.axiosInstance.post<{ classroom_id: string }>(
        `/invites/${encodeURIComponent(code)}/accept`,
        undefined,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error accepting invite:', error);
      throw error;
    }
  }

  /**
   * Status of a lesson link, or null when the code doesn't exist (never
   * existed, was reset, or the group was deleted). Never toasts: the waiting
   * screen polls it.
   */
  public async getLessonStatus(code: string): Promise<LessonStatus | null> {
    try {
      const { data } = await this.axiosInstance.get<LessonStatus>(
        `/lessons/${encodeURIComponent(code)}`,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      console.error('Error getting lesson status:', error);
      throw new Error('Error getting lesson status');
    }
  }

  /** «Вступить и войти»: join the group, then continue as a member. 409 when full. */
  public async joinLesson(code: string): Promise<LessonStatus> {
    try {
      const { data } = await this.axiosInstance.post<LessonStatus>(
        `/lessons/${encodeURIComponent(code)}/join`,
        undefined,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error joining lesson:', error);
      throw error;
    }
  }

  /** `title` names a lesson started by this call; without one it is named after its date. */
  public async startLesson(
    code: string,
    title: string | null = null
  ): Promise<LessonStartResponse> {
    try {
      const { data } = await this.axiosInstance.post<LessonStartResponse>(
        `/lessons/${encodeURIComponent(code)}/start`,
        { title }
      );
      return data;
    } catch (error) {
      console.error('Error starting lesson:', error);
      throw new Error('Error starting lesson');
    }
  }

  // ── Profile (logged-in account) ────────────────────────────────────────────

  public async getMeetingHistory(): Promise<MeetingHistoryItem[]> {
    try {
      const { data } = await this.axiosInstance.get('/meeting/history');
      return data;
    } catch (error) {
      console.error('Error getting meeting history:', error);
      throw new Error('Error getting meeting history');
    }
  }

  public async updateProfile(payload: { name?: string; email?: string }): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.patch('/auth/users/me', payload);
      return data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Error updating profile');
    }
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
    } catch (error) {
      console.error('Error changing password:', error);
      throw new Error('Error changing password');
    }
  }

  public async uploadAvatar(file: File): Promise<CurrentUser> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await this.axiosInstance.post('/auth/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return data;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw new Error('Error uploading avatar');
    }
  }

  public async deleteAccount(): Promise<void> {
    try {
      await this.axiosInstance.delete('/auth/users/me/account');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw new Error('Error deleting account');
    }
  }

  public async resendVerification(email: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/request-verify-token', { email });
    } catch (error) {
      console.error('Error requesting verification:', error);
      throw new Error('Error requesting verification');
    }
  }

  public async verifyEmail(token: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/verify', { token });
    } catch (error) {
      console.error('Error verifying email:', error);
      throw new Error('Error verifying email');
    }
  }

  public async attachMeetingMaterial(meetingUid: string, file: File) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/attach-material`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      console.error('Error attaching material:', error);
      throw new Error('Error attaching material');
    }
  }

  public async loginInMeeting({
    meetingUid,
    name,
    assistantInviteToken
  }: LoginInMeeting): Promise<LoginInMeetingResponse> {
    try {
      const response = await this.axiosInstance.post(
        '/participants/login-in-meeting',
        {
          meeting_uid: meetingUid,
          name,
          assistant_invite_token: assistantInviteToken
        },
        { skipErrorToast: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error login in meeting:', error);
      // 403: a study group lesson that doesn't let this visitor in. The
      // caller redirects with its own toast; everything else toasts here.
      const isForbidden = error instanceof AxiosError && error.response?.status === 403;
      if (
        !isForbidden &&
        !(error instanceof AxiosError && error.code === AxiosError.ERR_CANCELED)
      ) {
        showToast(
          'errorToast',
          'error',
          'Ошибка запроса',
          error instanceof Error ? error.message : undefined
        );
      }
      throw error;
    }
  }

  public async generateToken({ meetingUid, name }: RoomConnectParams): Promise<string> {
    try {
      const { data } = await this.axiosInstance.post('/rooms/room-token', {
        room_uid: meetingUid,
        name
      });
      return data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      throw error;
    }
  }

  public async createMeeting(params: CreateRoomParams) {
    try {
      const { data } = await this.axiosInstance.post('/meeting/', params);
      return data;
    } catch (error) {
      console.error('Error creating meeting:', error);
      throw new Error('Error creating meeting');
    }
  }

  public async getMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting meeting:', error);
      throw new Error('Error getting meeting');
    }
  }

  public async getCurrentMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/current`);
      return data;
    } catch (error) {
      console.error('Error getting current meeting:', error);
      throw new Error('Error getting current meeting');
    }
  }

  public async getStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/study-mode`);
      return data;
    } catch (error) {
      console.error('Error get study mode:', error);
      throw new Error('Error get study mode');
    }
  }

  public async changeStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/change-study-mode`);
      return data;
    } catch (error) {
      console.error('Error switching study mode:', error);
      throw new Error('Error switching study mode');
    }
  }

  public async authorDisconnect(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/finish`);
      return data;
    } catch (error) {
      console.error('Error disconnect from meeting:', error);
      throw new Error('Error disconnect from meeting');
    }
  }

  public async checkRoom(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/check`);
      return data;
    } catch (error) {
      console.error('Error checking room:', error);
      throw new Error('Error checking room');
    }
  }

  public async uploadMeetingPdf(meetingUid: string, file: File): Promise<DocProps> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/upload-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          // the caller shows its own message (e.g. the 5-file limit)
          skipErrorToast: true
        }
      );

      return data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  public async getCurrentFileForMeeting(meetingUid: string): Promise<string> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/current-file`);

      return data;
    } catch (error) {
      console.error('Error getting the file:', error);
      throw new Error('Error getting the file');
    }
  }

  public async getActiveFileForMeeting(
    meetingUid: string,
    signal?: AbortSignal
  ): Promise<DocProps> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/active-file`, {
        signal
      });

      return data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      console.error('Error getting the active file:', error);
      throw new Error('Error getting the active file');
    }
  }

  public async getAllFiles(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/files`);

      return data;
    } catch (error) {
      console.error('Error getting files:', error);
      throw new Error('Error getting files');
    }
  }

  public async setActiveFile(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/active-file/${fileId}`
      );
      return data;
    } catch (error) {
      console.error('Error setting active file:', error);
      throw new Error('Error setting active file');
    }
  }

  public async setUnActiveFile(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/files/meeting/${meetingUid}/unactive-file`);
      return data;
    } catch (error) {
      console.error('Error setting unactive file:', error);
      throw new Error('Error setting unactive file');
    }
  }

  public async getUploadFiles(meetingUid: string, signal?: AbortSignal): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/upload-files`, {
        signal
      });
      return data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      console.error('Error getting upload files', error);
      throw new Error('Error getting upload files');
    }
  }

  public async getStudentFiles(meetingUid: string, signal?: AbortSignal): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/student-files`, {
        signal
      });
      return data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      console.error('Error getting student files', error);
      throw new Error('Error getting student files');
    }
  }

  public async shareFileWithStudentInMeeting(
    meetingUid: string,
    fileId: string
  ): Promise<GetMessages> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/share-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error sharing file with students');
      throw new Error('Error sharing file with students');
    }
  }

  public async unshareFileWithStudentInMeeting(
    meetingUid: string,
    fileId: string
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/unshare-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error unsharing file with students');
      throw new Error('Error unsharing file with students');
    }
  }

  public async deleteFileFromMeeting(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.delete(`/files/meeting/${meetingUid}/${fileId}`);
      return data;
    } catch (error) {
      console.error('Error deleting file from meeting', error);
      throw new Error('Error deleting file from meeting');
    }
  }

  public async getTeacherBoards(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/boards/teacher-boards/${meetingUid}`);

      return data;
    } catch (error) {
      console.error('Error getting teacher boards:', error);
      throw new Error('Error getting teacher boards');
    }
  }

  public async getBoardSnapshots(meetingUid: string): Promise<BoardSnapshotItem[]> {
    try {
      const { data } = await this.axiosInstance.get(`/boards/${meetingUid}/snapshots`);
      return data;
    } catch (error) {
      console.error('Error getting board snapshots:', error);
      throw new Error('Error getting board snapshots');
    }
  }

  // The raw RoomSnapshot JSON as tldraw-sync stored it (see utils/boardSnapshot.ts)
  public async getBoardSnapshot(meetingUid: string, roomId: string): Promise<unknown> {
    try {
      const { data } = await this.axiosInstance.get(
        `/boards/${meetingUid}/snapshots/${encodeURIComponent(roomId)}`,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error getting board snapshot:', error);
      throw new Error('Error getting board snapshot');
    }
  }

  public async disconnectParticipant(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(
        `/participants/meeting/${meetingUid}/disconnect-participant`
      );
      return data;
    } catch (error) {
      console.error('Error disconnecting participant:', error);
      throw new Error('Error disconnecting participant');
    }
  }

  public async getNameParticipant(meetingUid: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.post<string | null>(
        `/participants/meeting/${meetingUid}/get-name-participant`
      );
      return data;
    } catch (error) {
      console.error('Error getting name of participant:', error);
      throw new Error('Error getting name of participant');
    }
  }

  public async getLinkTokenAssistant(meetingUid: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${meetingUid}/get-invite-token-to-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting token to invite assistant:', error);
      throw new Error('Error getting token to invite assistant');
    }
  }

  public async getClassroomLinks(meetingUid: string): Promise<ClassroomLinks | null> {
    try {
      const { data } = await this.axiosInstance.get<ClassroomLinks | null>(
        `/meeting/${meetingUid}/classroom-links`,
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error getting classroom links:', error);
      throw new Error('Error getting classroom links');
    }
  }

  public async getMeetingByAssistant(assistantInviteToken: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${assistantInviteToken}/get-meeting-by-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting meeting by assistant:', error);
      throw new Error('Error getting meeting by assistant');
    }
  }

  public async createRoom(meetingUid: string, room: CreateEditMeetingRoom): Promise<MeetingRoom> {
    try {
      // Callers show their own "couldn't create a room" toast.
      const { data } = await this.axiosInstance.post(
        `/rooms/${meetingUid}`,
        { name: room.name },
        { skipErrorToast: true }
      );
      return data;
    } catch (error) {
      console.error('Error creating room:', error);
      throw new Error('Error creating room');
    }
  }

  public async editRoom(
    meetingUid: string,
    roomId: number,
    room: CreateEditMeetingRoom
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/rooms/${meetingUid}/${roomId}`, {
        name: room.name
      });
      return data;
    } catch (error) {
      console.error('Error editing room:', error);
      throw new Error('Error editing room');
    }
  }

  public async getRooms(meetingUid: string): Promise<MeetingRoom[]> {
    try {
      const { data } = await this.axiosInstance.get(`/rooms/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting rooms:', error);
      throw new Error('Error getting rooms');
    }
  }

  public async getChats(meetingUid: string): Promise<MeetingChat[]> {
    try {
      const { data } = await this.axiosInstance.get(`/chats/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting chats', error);
      throw new Error('Error getting chats');
    }
  }

  public async getPrivatePartners(meetingUid: string): Promise<ChatCandidate[]> {
    try {
      const { data } = await this.axiosInstance.get(`/chats/${meetingUid}/private-partners`);
      return data;
    } catch (error) {
      console.error('Error getting private chat partners', error);
      throw new Error('Error getting private chat partners');
    }
  }

  public async getLastMessages(meetingUuid: string): Promise<LastMessage[]> {
    try {
      const { data } = await this.axiosInstance.get('/messages/last', {
        params: { meeting_uuid: meetingUuid }
      });
      return data;
    } catch (error) {
      console.error('Error getting last messages', error);
      throw new Error('Error getting last messages');
    }
  }

  public async getChatCandidates(meetingUid: string): Promise<ChatCandidate[]> {
    try {
      const { data } = await this.axiosInstance.get(`/chats/${meetingUid}/candidates`);
      return data;
    } catch (error) {
      console.error('Error getting chat candidates', error);
      throw new Error('Error getting chat candidates');
    }
  }

  public async createChat(meetingUid: string, chat: CreateEditChat): Promise<MeetingChat> {
    try {
      const { data } = await this.axiosInstance.post(`/chats/${meetingUid}`, chat);
      return data;
    } catch (error) {
      console.error('Error creating chat', error);
      throw new Error('Error creating chat');
    }
  }

  public async editChat(
    meetingUid: string,
    chatId: string,
    chat: CreateEditChat
  ): Promise<MeetingChat> {
    try {
      const { data } = await this.axiosInstance.put(`/chats/${meetingUid}/${chatId}`, chat);
      return data;
    } catch (error) {
      console.error('Error editing chat', error);
      throw new Error('Error editing chat');
    }
  }

  public async deleteChat(meetingUid: string, chatId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.delete(`/chats/${meetingUid}/${chatId}`);
      return data;
    } catch (error) {
      console.error('Error deleting chat', error);
      throw new Error('Error deleting chat');
    }
  }

  public async uploadChatImage(meetingUuid: string, file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/messages/upload-image?meeting_uuid=${meetingUuid}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      console.error('Error uploading chat image', error);
      throw new Error('Error uploading chat image');
    }
  }

  public async sendMessage(message: SendMessage): Promise<boolean | undefined> {
    try {
      const { data } = await this.axiosInstance.post('/messages/send-message', message);
      return data;
    } catch (error) {
      console.error('Error sending message', error);
      throw new Error('Error sending message');
    }
  }

  public async getMessages(
    params: GetMessagesParams,
    signal?: AbortSignal
  ): Promise<GetMessages[]> {
    try {
      const { data } = await this.axiosInstance.get('/messages/', { params, signal });
      return data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      console.error('Error getting messages', error);
      throw new Error('Error getting messages');
    }
  }

  public async updateSettings(meetingUid: string, settings: MeetingSettingsApi): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/settings`, settings);
      return data;
    } catch (error) {
      console.error('Error updating settings', error);
      throw new Error('Error updating settings');
    }
  }

  public async createMeetingSettingTemplate(
    template: CreateMeetingSettingTemplate
  ): Promise<MeetingSettingTemplateApi | null> {
    try {
      const { data } = await this.axiosInstance.post('/meeting-templates/', template);
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        clearToast('errorToast');
        showToast(
          'warningToast',
          'warn',
          'Создавать шаблоны могут только зарегистрированные пользователи'
        );
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        clearToast('errorToast');
        showToast(
          'warningToast',
          'warn',
          error.response.data?.detail ?? 'Не удалось сохранить шаблон'
        );
        return null;
      }
      console.error('Error creating meeting setting template', error);
      throw new Error('Error creating meeting setting template');
    }
  }

  public async getMeetingSettingTemplates(): Promise<MeetingSettingTemplateApi[]> {
    try {
      const { data } = await this.axiosInstance.get('/meeting-templates/');
      return data;
    } catch (error) {
      console.error('Error getting meeting setting templates', error);
      throw new Error('Error getting meeting setting templates');
    }
  }

  public async deleteMeetingSettingTemplate(templateId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.delete(`/meeting-templates/${templateId}`);
      return data;
    } catch (error) {
      console.error('Error deleting meeting setting template', error);
      throw new Error('Error deleting meeting setting template');
    }
  }

  public async handUp(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/participants/meeting/${meetingUid}/hand-up`);
      return data;
    } catch (error) {
      console.error('Error of hand up', error);
      throw new Error('Error of hand up');
    }
  }

  public async handDown(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/hand-down`
      );
      return data;
    } catch (error) {
      console.error('Error of hand down', error);
      throw new Error('Error of hand down');
    }
  }

  public async handUpInRoom(meetingUid: string, roomId: number): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/rooms/${meetingUid}/${roomId}/hand-up-in-room`
      );
      return data;
    } catch (error) {
      console.error('Error of hand up in room', error);
      throw new Error('Error of hand up in room');
    }
  }

  public async handDownInRoom(meetingUid: string, roomId: number): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/rooms/${meetingUid}/${roomId}/hand-down-in-room`
      );
      return data;
    } catch (error) {
      console.error('Error of hand down in room', error);
      throw new Error('Error of hand down in room');
    }
  }

  public async getHandUpOfRooms(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/rooms/${meetingUid}/hand-up`);
      return data;
    } catch (error) {
      console.error('Error getting hand up rooms', error);
      throw new Error('Error getting hand up rooms');
    }
  }

  public async getHandUpOfStudents(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/participants/${meetingUid}/hand-up`);
      return data;
    } catch (error) {
      console.error('Error getting hand up students', error);
      throw new Error('Error getting hand up students');
    }
  }

  public async editMeetingTitle(meetingUid: string, new_title: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/edit-title`, {
        new_title: new_title
      });
      return data;
    } catch (error) {
      console.error('Error editing title of meeting', error);
      throw new Error('Error editing title of meeting');
    }
  }

  public async broadcastHardMuteRoleSignals(
    meetingUid: string,
    role: ParticipantRole
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/mute-role`, {
        role_to_mute: role
      });
      return data;
    } catch (error) {
      console.error('Error hard muting for section', error);
      throw new Error('Error hard muting for section');
    }
  }

  public async broadcastOffHardMuteRoleSignals(
    meetingUid: string,
    role: ParticipantRole
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/off-mute-role`, {
        role_to_mute: role
      });
      return data;
    } catch (error) {
      console.error('Error turn off hard muting for section', error);
      throw new Error('Error turn off hard muting for section');
    }
  }

  public async broadcastHardMuteParticipantSignals(
    meetingUid: string,
    userUid: string
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/mute-participant`, {
        user_uuid: userUid
      });
      return data;
    } catch (error) {
      console.error('Error hard muting for participant', error);
      throw new Error('Error hard muting for participant');
    }
  }

  public async broadcastOffHardMuteParticipantSignals(
    meetingUid: string,
    userUid: string
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/meeting/${meetingUid}/off-mute-participant`,
        {
          user_uuid: userUid
        }
      );
      return data;
    } catch (error) {
      console.error('Error turn off hard muting for participant', error);
      throw new Error('Error turn off hard muting for participant');
    }
  }

  public async setScreenSharePermission(
    meetingUid: string,
    userUid: string,
    allowed: boolean
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/meeting/${meetingUid}/screen-share-permission`,
        {
          user_uuid: userUid,
          allowed
        }
      );
      return data;
    } catch (error) {
      console.error('Error updating screen-share permission', error);
      throw new Error('Error updating screen-share permission');
    }
  }

  // ── Admin Panel (Superuser only) ──────────────────────────────────────────

  public async getAdminMeetings(
    offset = 0,
    limit = 25
  ): Promise<AdminListResponse<AdminMeetingItem>> {
    try {
      const { data } = await this.axiosInstance.get<AdminListResponse<AdminMeetingItem>>(
        `/admin/meetings?offset=${offset}&limit=${limit}`
      );
      return data;
    } catch (error) {
      console.error('Error getting admin meetings:', error);
      throw new Error('Error getting admin meetings');
    }
  }

  public async getAdminUsers(offset = 0, limit = 25): Promise<AdminListResponse<AdminUserItem>> {
    try {
      const { data } = await this.axiosInstance.get<AdminListResponse<AdminUserItem>>(
        `/admin/users?offset=${offset}&limit=${limit}`
      );
      return data;
    } catch (error) {
      console.error('Error getting admin users:', error);
      throw new Error('Error getting admin users');
    }
  }

  public async getAdminClassrooms(
    offset = 0,
    limit = 25
  ): Promise<AdminListResponse<AdminClassroomItem>> {
    try {
      const { data } = await this.axiosInstance.get<AdminListResponse<AdminClassroomItem>>(
        `/admin/classrooms?offset=${offset}&limit=${limit}`
      );
      return data;
    } catch (error) {
      console.error('Error getting admin classrooms:', error);
      throw new Error('Error getting admin classrooms');
    }
  }

  public async adminFinishMeeting(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put<boolean>(
        `/admin/meetings/${meetingUid}/finish`
      );
      return data;
    } catch (error) {
      console.error('Error finishing meeting by admin:', error);
      throw new Error('Error finishing meeting');
    }
  }
}

export default new Api();
